import { Assignment, Business, ImageNotSupported, Inventory, Label, LocationCity, LocationOn, Mail, Person, PersonOutline, Phone, Sms, Warehouse } from '@mui/icons-material';
import { Avatar, AvatarProps, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { AppModels, AppState, BrandAttrs, CompanyAttrs, ContactAttrs, FileAttrs, Lang, LocationAttrs, ProductAttrs, ProductCategoryAttrs, ProjectAttrs, RelatedContactAttrs, TagAttrs, UserAttrs, WarehouseAttrs, brandSelector, companySelector, contactSelector, fetchBrand, fetchCompany, fetchContact, fetchFile, fetchLocation, fetchProduct, fetchProductCategory, fetchProject, fetchRelatedContact, fetchTag, fetchUser, fetchWarehouse, fileSelector, locationSelector, notEmptyString, productCategorySelector, productSelector, projectSelector, relatedContactSelector, tagSelector, userSelector, warehouseSelector } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { appStore } from '../store';


export enum WarehouseCodeMode {
    MAIN = 'MAIN',
    ALL = 'ALL',
    CURRENT = 'CURRENT'
}

export const ShowWarehouseCode = (props: { id: ModelId | null, mode: WarehouseCodeMode; }) => {
    const warehouse = useSelector((state: AppState) => warehouseSelector.selectById(state, props.id ?? ''));
    if (props.id) {
        if (warehouse) {
            switch (props.mode) {
                case WarehouseCodeMode.ALL:
                    return <>{warehouse.parentWarehouseId ? <><ShowWarehouseCode id={warehouse.parentWarehouseId} mode={WarehouseCodeMode.ALL} />{' / '}</> : null}<WarehouseLoader id={props.id} render={p => p.code ?? p.name.substring(0, 6)} /></>;
                case WarehouseCodeMode.CURRENT:
                    return <WarehouseLoader id={props.id} render={p => p.code ?? p.name.substring(0, 6)} />;
                case WarehouseCodeMode.MAIN:
                    return warehouse.parentWarehouseId ? <ShowWarehouseCode id={warehouse.parentWarehouseId} mode={WarehouseCodeMode.MAIN} /> : <WarehouseLoader id={props.id} render={p => p.code ?? p.name.substring(0, 6)} />;
            }
        } else {
            return <WarehouseLoader id={props.id} />;
        }
    } else return null;
};

export type AppItemRenderMode = 'full' | 'compact' | 'text' | 'code';

export const AppItem = (props: {
    type: AppModels,
    id: ModelId,
    placeholder?: string,
    onEmpty?: React.ReactNode;
    onEdit?: () => void,
    mode?: AppItemRenderMode;
}) => {

    switch (props.type) {
        case AppModels.products:
            return <ProductLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.users:
            return <UserLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.companies:
            return <CompanyLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.brands:
            return <BrandLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.tags:
            return <TagLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.warehouses:
            return <WarehouseLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.locations:
            return <LocationLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.contacts:
            return <ContactLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.projects:
            return <ProjectLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        case AppModels.productCategories:
            return <ProductCategoryLoader id={props.id} render={model => <LoadedAppItem {...props} type={props.type} model={model} />} />;
        default:
            return props.onEmpty ? props.onEmpty : (props.placeholder ?? 'Élément inconnu');
    }
};

export const ProductLoader = (props: { id?: ModelId | null, render?: (model: ProductAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? productSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchProduct(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};
export const UserLoader = (props: { id?: ModelId | null, render?: (model: UserAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? userSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchUser(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};

export const CompanyLoader = (props: { id?: ModelId | null, render?: (model: CompanyAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? companySelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchCompany(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};

export const ProjectLoader = (props: { id?: ModelId | null, render?: (model: ProjectAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? projectSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchProject(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};

export const ProductCategoryLoader = (props: { id?: ModelId | null, render?: (model: ProductCategoryAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? productCategorySelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchProductCategory(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};

export const BrandLoader = (props: { id?: ModelId | null, render?: (model: BrandAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? brandSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchBrand(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};

export const TagLoader = (props: { id?: ModelId | null, render?: (model: TagAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? tagSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchTag(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};
export const WarehouseLoader = (props: { id?: ModelId | null, render?: (model: WarehouseAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? warehouseSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchWarehouse(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};
export const LocationLoader = (props: { id?: ModelId | null, render?: (model: LocationAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? locationSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchLocation(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};
export const ContactLoader = (props: { id?: ModelId | null, render?: (model: ContactAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? contactSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchContact(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};
export const RelatedContactLoader = (props: { id?: ModelId | null, render?: (model: RelatedContactAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? relatedContactSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchRelatedContact(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};
export const FileLoader = (props: { id?: ModelId | null, render?: (model: FileAttrs) => React.ReactNode, onEmpty?: React.ReactNode; }) => {
    const model = useSelector((state: AppState) => props.id ? fileSelector.selectById(state, props.id) : undefined);
    React.useEffect(() => {
        if (props.id && !model) {
            appStore.dispatch(fetchFile(props.id));
        }
    }, [props.id, model]);

    return model ? (props.render ? props.render(model) : null) : (props.onEmpty ?? null);
};


/*
<Grid container alignItems="center">
                            <Grid item sx={{ display: 'flex', width: 44 }}>
                                <LocationOnIcon sx={{ color: 'text.secondary' }} />
                            </Grid>
                            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                                {parts.map((part: any, index: number) => (
                                    <Box
                                        key={index}
                                        component="span"
                                        sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                                <Typography variant="body2" color="text.secondary">
                                    {option.structured_formatting.secondary_text}
                                </Typography>
                            </Grid>
                        </Grid>
                        */


export const NoCompanyIcon = <LocationCity />;
export const NoProductIcon = <Inventory />;
export const AppItemAvatar = (props: AvatarProps & {
    type: AppModels,
    model: any;
}): React.ReactNode => {
    const { type, model, ...avatarProps } = props;
    const navigate = useNavigate();
    switch (props.type) {
        case AppModels.products:
            const product: ProductAttrs = props.model as ProductAttrs;
            return product && product.image ? <Avatar src={product.image} {...avatarProps} /> : <Avatar {...avatarProps}><Inventory /></Avatar>;
        case AppModels.users:
            const user: UserAttrs = props.model as UserAttrs;
            return user && user.image ? <Avatar src={user.image} {...avatarProps} /> : <Avatar {...avatarProps}><Person /></Avatar>;
        case AppModels.companies:
            const company: CompanyAttrs = props.model as CompanyAttrs;
            return company && company.imageFileId ? <FileLoader id={String(company.imageFileId)} render={file => <Avatar src={file.thumbUrl ?? file.url} {...avatarProps} />} /> : <Avatar {...avatarProps}>{NoCompanyIcon}</Avatar>;
        case AppModels.brands:
            const brand: BrandAttrs = props.model as BrandAttrs;
            return brand && brand.imageFileId ? <FileLoader id={String(brand.imageFileId)} render={file => <Avatar src={file.thumbUrl ?? file.url} {...avatarProps} />} /> : <Avatar {...avatarProps}><Business /></Avatar>;
        case AppModels.contacts:
            const contact: ContactAttrs = props.model as ContactAttrs;
            return contact && contact.imageFileId ? <FileLoader id={String(contact.imageFileId)} render={file => <Avatar src={file.thumbUrl ?? file.url} {...avatarProps} />} /> : <Avatar {...avatarProps}><PersonOutline /></Avatar>;
        case AppModels.tags:
            return <Avatar {...avatarProps}><Label /></Avatar>;
        case AppModels.warehouses:
            return <Avatar {...avatarProps}><Warehouse /></Avatar>;
        case AppModels.projects:
            return <Avatar {...avatarProps}><Assignment /></Avatar>;
        case AppModels.locations:
            const location: LocationAttrs = props.model as LocationAttrs;
            return location ? <Avatar onClick={() => {
                navigate('https://www.google.com/maps/place/' + encodeURIComponent(location.name));
            }} {...avatarProps}><LocationOn htmlColor='red' /></Avatar> : <Avatar {...avatarProps}><LocationOn htmlColor='grey' /></Avatar>;
        default:
            return <Avatar {...avatarProps}><ImageNotSupported /></Avatar>;
    }
};

const AppItemPrimary = (props: {
    type: AppModels,
    model: any;
}): string => {
    switch (props.type) {
        case AppModels.products:
            const product: ProductAttrs = props.model as ProductAttrs;
            return product.name;
        case AppModels.productCategories:
            return (props.model as ProductCategoryAttrs).name;
        case AppModels.users:
            const user: UserAttrs = props.model as UserAttrs;
            return user.name;
        case AppModels.companies:
            const company: CompanyAttrs = props.model as CompanyAttrs;
            return company.name;
        case AppModels.brands:
            const brand: BrandAttrs = props.model as BrandAttrs;
            return brand.name;
        case AppModels.contacts:
            const contact: ContactAttrs = props.model as ContactAttrs;
            return contact.name;
        case AppModels.tags:
            return (props.model as TagAttrs).name;
        case AppModels.warehouses:
            return (props.model as WarehouseAttrs).name;
        case AppModels.projects:
            return (props.model as ProjectAttrs).name;
        case AppModels.locations:
            return (props.model as LocationAttrs).name;

        default:
            return 'élément inconnu';
    }
};

export const ContactIcons = (props: { model: ContactAttrs; }) => {
    const email = () => {
        return props.model.email ? <a href={'mailto:' + props.model.email} target="_blank"><Mail /></a> : null;
    };
    const sms = () => {
        return props.model.cell ? <a href={'sms:' + props.model.cell} target="_blank"><Sms /></a> : null;
    };
    const phone = () => {
        return props.model.cell ? <a href={'tel:' + props.model.cell} target="_blank"><Phone /></a> : (props.model.tel ? <a href={'tel://' + props.model.tel} target="_blank"><Phone /></a> : null);
    };
    return <Typography component='span' fontSize={8}>{email()}{sms()}{phone()}</Typography>;
};

const SecondaryContact = (props: { model: ContactAttrs; }) => {

    const titre = () => <Typography noWrap fontSize={9}>{props.model.relations.map((p, index) => {
        return <>{index === 0 ? '' : ', '}{p.label + ' @ '}<AppItem type={p.type} id={p.id} mode='code' /></>;
    })}</Typography>;

    return <><ContactIcons model={props.model} />{titre()}</>;
};

export const AppItemSecondary = (props: {
    type: AppModels,
    model: any;
}): React.ReactNode => {
    switch (props.type) {
        case AppModels.products:
            const product: ProductAttrs = props.model as ProductAttrs;
            return product.code;
        case AppModels.users:
            const user: UserAttrs = props.model as UserAttrs;
            return user.roles.map(p => Lang('roles.' + p.role)).join(', ');
        case AppModels.companies:
            const company: CompanyAttrs = props.model as CompanyAttrs;
            return <Typography fontSize={10} variant='subtitle1'>{notEmptyString(company.type) ?? 'Compagnie / entreprise'}</Typography>;
        case AppModels.brands:
            return <Typography fontSize={10} variant='subtitle1'>Marque de commerce</Typography>;
        case AppModels.contacts:
            const contact: ContactAttrs = props.model as ContactAttrs;
            return <SecondaryContact model={contact} />;
        case AppModels.tags:
            return '';
        case AppModels.warehouses:
            return <Typography fontSize={10} variant='subtitle1'><ShowWarehouseCode id={props.model.id} mode={WarehouseCodeMode.ALL} /></Typography>;
        case AppModels.projects:
            const project = props.model as ProjectAttrs;
            return <Typography fontSize={10} variant='subtitle1'>{project.code}</Typography>;
        default:
            return '';
    }
};
export const LoadedAppItem = (props: {
    type: AppModels,
    model: any;
    placeholder?: string;
    onEdit?: () => void,
    mode?: AppItemRenderMode;
}) => {

    switch (props.mode) {
        case 'full':
            return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <Box>
                    <AppItemAvatar model={props.model} type={props.type} sx={{ width: 40 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant='subtitle1' noWrap overflow='hidden'><AppItemPrimary model={props.model} type={props.type} /></Typography>
                    <Typography variant='body2' noWrap overflow='hidden'><AppItemSecondary model={props.model} type={props.type} /></Typography>
                </Box>
            </Box>;
        case 'compact':
            return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, minHeight: 20, alignItems: 'center' }}>
                <Box sx={{ width: 20 }}>
                    <AppItemAvatar model={props.model} type={props.type} sx={{ width: 20 }} />
                </Box>
                <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center' }}>
                    <Typography component='div' variant='body1' overflow='hidden' noWrap><AppItemPrimary model={props.model} type={props.type} /></Typography>
                </Box>
            </Box>;
        case 'code':
            if (props.model?.code) {
                return props.model.code;
            }
        case 'text':
        default:
            return <AppItemPrimary model={props.model} type={props.type} />;
    }
};
